import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bitcoin, Menu, X, PenSquare, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CATEGORIES } from '@/lib/categories';
import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { AUTHOR_PUBKEY } from '@/lib/categories';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useCurrentUser();

  const isAuthor = user?.pubkey === AUTHOR_PUBKEY;

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Bitcoin className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">wtfisBitcoin</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-40 h-full w-72 bg-sidebar border-r border-sidebar-border transform transition-transform duration-300 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-sidebar-border">
            <Link to="/" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-lg animate-glow">
                <Bitcoin className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-sidebar-foreground">wtfisBitcoin</h1>
                <p className="text-xs text-muted-foreground">What the Fuck is Bitcoin?</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-4">
            {/* Bitcoin-Duden - Featured Link */}
            <div className="mb-6">
              <Link
                to="/duden"
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-4 rounded-xl transition-all duration-200 group border-2',
                  location.pathname === '/duden'
                    ? 'bg-primary/10 border-primary text-sidebar-foreground'
                    : 'border-primary/30 hover:border-primary hover:bg-primary/5 text-sidebar-foreground'
                )}
              >
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
                  location.pathname === '/duden'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-primary/10 group-hover:bg-primary/20'
                )}>
                  <BookOpen className="w-5 h-5 text-primary group-hover:text-primary" />
                </div>
                <div>
                  <span className="font-semibold text-sm">Bitcoin-Duden</span>
                  <p className="text-xs text-muted-foreground">Alle Begriffe erklärt</p>
                </div>
              </Link>
            </div>

            {/* Themengebiete Label - Mobile */}
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 mb-2 lg:hidden">
              Themengebiete
            </p>

            {/* Categories */}
            <div className="space-y-1">
              {CATEGORIES.map((category) => {
                const Icon = category.icon;
                const isActive = location.pathname === `/kategorie/${category.id}`;

                return (
                  <Link
                    key={category.id}
                    to={`/kategorie/${category.id}`}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group',
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'hover:bg-sidebar-accent/50 text-sidebar-foreground'
                    )}
                  >
                    <Icon
                      className={cn(
                        'w-5 h-5 transition-colors',
                        isActive ? 'text-primary' : 'text-primary/70 group-hover:text-primary'
                      )}
                    />
                    <span className="font-medium text-sm">{category.name}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-sidebar-border space-y-3">
            {isAuthor && (
              <Link to="/editor" onClick={() => setSidebarOpen(false)}>
                <Button className="w-full gap-2" variant="default">
                  <PenSquare className="w-4 h-4" />
                  Neuer Artikel
                </Button>
              </Link>
            )}
            <LoginArea className="w-full" />
            <p className="text-xs text-center text-muted-foreground">
              Vibed with{' '}
              <a
                href="https://shakespeare.diy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Shakespeare
              </a>
            </p>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-72 pt-16 lg:pt-0 min-h-screen">
        {children}
      </main>
    </div>
  );
}
